import { PlayIcon, XIcon } from '@phosphor-icons/react'
import { ReactNode, useRef, useState } from 'react'

import { Icon } from '@/components/ui/icon'
import { Overlay } from '@/components/ui/overlay'
import { Typography } from '@/components/ui/typography'

import * as S from './tutorial-modal.styled'

interface TutorialModalProps {
  onClose: () => void
  content?: {
    title: string
    description: string
    bullets: string[]
    footer: ReactNode
  }
  video?: {
    alt: string
    url: string
    previewUrl: string
  }
}

export const TutorialModal = ({ content, onClose, video }: TutorialModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playOverlayDismissed, setPlayOverlayDismissed] = useState(false)

  const handlePlayOverlayClick = () => {
    if (!video?.url) {
      return
    }
    void videoRef.current?.play()
    setPlayOverlayDismissed(true)
  }

  const showPlayOverlay = Boolean(video?.url) && !playOverlayDismissed

  return (
    <Overlay onClose={onClose}>
      {({ getFloatingProps, setFloating }) => (
        <S.Panel ref={setFloating} {...getFloatingProps()}>
          {content && (
            <S.ContentSection>
              <S.Title>{content.title}</S.Title>
              <S.Description>{content.description}</S.Description>

              <S.HowItWorksHeader>
                <img alt="" src="/images/Star3D.svg" width="24" height="24" />
                <Typography size="text-medium" weight="semibold">
                  How it works
                </Typography>
              </S.HowItWorksHeader>

              <S.BulletList>
                {content.bullets.map((bullet) => (
                  <S.BulletItem key={bullet}>{bullet}</S.BulletItem>
                ))}
              </S.BulletList>

              {content.footer}
            </S.ContentSection>
          )}

          {video && (
            <S.VideoSection>
              <S.PreviewWrapper>
                <S.Video
                  ref={videoRef}
                  controls
                  playsInline
                  poster={video.previewUrl}
                  preload="metadata"
                  src={video.url}
                />
                {showPlayOverlay && (
                  <S.PlayOverlay type="button" onClick={handlePlayOverlayClick}>
                    <Icon icon={PlayIcon} size="2.8rem" weight="fill" />
                  </S.PlayOverlay>
                )}
              </S.PreviewWrapper>
            </S.VideoSection>
          )}

          <S.CloseButton onClick={onClose}>
            <Icon icon={XIcon} size="2.4rem" />
          </S.CloseButton>
        </S.Panel>
      )}
    </Overlay>
  )
}
